/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

/**
 * Speaking and listening for the help panel, on the browser's own engines.
 *
 * Nothing here goes near our servers: dictation uses the Web Speech API and reading uses
 * speechSynthesis, so a spoken question costs nothing, adds no latency of ours, and no recording of
 * anyone's voice is stored or transmitted by us. The trade is that both depend on what the user's
 * browser and operating system happen to provide — see {@link speechSupport}.
 */

/**
 * Portal language → the locale tag the speech engines expect.
 *
 * The portal's codes are language-only ("ru", "pt"); the speech APIs want a region ("ru-RU", "pt-PT"),
 * and a bare code selects a wrong voice or none at all. Chinese already carries its script and maps to
 * the region that script is written in.
 */
const SPEECH_LOCALES: Readonly<Record<string, string>> = {
  en: 'en-US',
  es: 'es-ES',
  de: 'de-DE',
  ru: 'ru-RU',
  fr: 'fr-FR',
  pt: 'pt-PT',
  tr: 'tr-TR',
  uk: 'uk-UA',
  fa: 'fa-IR',
  hy: 'hy-AM',
  ka: 'ka-GE',
  ur: 'ur-PK',
  uz: 'uz-UZ',
  'zh-Hans': 'zh-CN',
  'zh-Hant': 'zh-TW',
};

/** "ru" → "ru-RU"; an unknown code is passed through so a new language still gets its best try. */
export const speechLocale = (lang: string): string =>
  SPEECH_LOCALES[lang] || SPEECH_LOCALES[lang?.split('-')[0]] || lang || 'en-US';

type SpeechRecognitionCtor = new () => any;

const recognitionCtor = (): SpeechRecognitionCtor | null => {
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

/**
 * What this browser can actually do. Dictation is Chrome, Edge and Safari only — Firefox ships no
 * recognition engine — so the microphone has to hide rather than fail, and reading aloud is available
 * nearly everywhere but with voices that depend on the operating system.
 */
export const speechSupport = () => ({
  canListen: !!recognitionCtor(),
  canSpeak: typeof window !== 'undefined' && 'speechSynthesis' in window,
});

/**
 * Does the machine have a voice for this language at all?
 *
 * Asked before offering to read anything: a Georgian answer read by an English voice is worse than
 * silence. Voices load asynchronously in some browsers, so an empty list means "not yet", not "never" —
 * the caller re-checks on the voiceschanged event.
 */
export const hasVoiceFor = (locale: string): boolean => {
  if (!('speechSynthesis' in window)) return false;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return false;
  const base = locale.split('-')[0].toLowerCase();
  return voices.some((v) => v.lang.toLowerCase().replace('_', '-').split('-')[0] === base);
};

/** The best available voice for a locale: an exact region match first, then any voice of that language. */
const pickVoice = (locale: string): SpeechSynthesisVoice | null => {
  const voices = window.speechSynthesis.getVoices();
  const want = locale.toLowerCase().replace('_', '-');
  const base = want.split('-')[0];
  return (
    voices.find((v) => v.lang.toLowerCase().replace('_', '-') === want)
    || voices.find((v) => v.lang.toLowerCase().replace('_', '-').split('-')[0] === base)
    || null
  );
};

/**
 * Markdown as it should be heard.
 *
 * The answers are written for the eye — **bold** button names, numbered steps, `code`, arrows — and a
 * synthesiser reads every one of those characters out loud ("star star Add Vehicle star star"). This
 * keeps the words and the structure a listener needs and drops the typography they do not.
 */
export const speakableText = (markdown: string): string =>
  markdown
    .replace(/```[\s\S]*?```/g, ' ')             // fenced code is not speech
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')     // keep the link text, drop the URL
    .replace(/^\s{0,3}#{1,6}\s*/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/^\s*[-*]\s+/gm, '')                // bullets: the pause does the work
    .replace(/→/g, ', ')                         // "Settings → Owner Settings" reads as a breath
    .replace(/[|_>]/g, ' ')
    .replace(/\r/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();

/**
 * Split text into utterances a listener can follow.
 *
 * Long paragraphs are handed to the engine in one piece by naive code, which makes stopping feel
 * unresponsive and, in several browsers, silently truncates. Sentence-sized pieces also let an answer be
 * read WHILE it is still streaming in: whatever is complete can be spoken, and the rest follows.
 */
export const speechChunks = (text: string): string[] =>
  text
    // A line break always ends an utterance; a full stop only when it is not part of a number.
    // Without that last guard "1. Open All Vehicles" is read as "one" … "open all vehicles", and
    // "$20.79" as "twenty" … "seventy-nine" — a numbered list and an amount are exactly what a help
    // answer is full of. CJK terminators need no space after them, so they split on the mark itself.
    .split(/\n+|(?<=[。！？])|(?<=[^0-9\s][.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

/**
 * Read text aloud in the given locale. Returns false when the machine has no voice for that language,
 * so the caller can leave the toggle off rather than produce nonsense.
 */
export const speak = (text: string, locale: string): boolean => {
  if (!('speechSynthesis' in window)) return false;
  const voice = pickVoice(locale);
  if (!voice) return false;

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.voice = voice;
  utterance.lang = voice.lang || locale;
  window.speechSynthesis.speak(utterance);
  return true;
};

/**
 * Unlock speech on a phone.
 *
 * iOS Safari only lets speechSynthesis start from inside a user gesture, and the help panel's reading
 * begins seconds later, when the model's first sentence arrives — by then the gesture is over and iOS
 * drops the utterance without an error. Speaking one silent utterance during the tap that asks the
 * question marks the synthesiser as user-approved, and everything after it is allowed. Harmless
 * elsewhere: on a desktop this queues and finishes an empty string.
 */
export const primeSpeech = (): void => {
  if (!('speechSynthesis' in window)) return;
  const silent = new SpeechSynthesisUtterance(' ');
  silent.volume = 0;
  window.speechSynthesis.speak(silent);
};

/** Stop mid-sentence. Called when the panel closes, the user navigates, or asks something new. */
export const stopSpeaking = (): void => {
  if ('speechSynthesis' in window) window.speechSynthesis.cancel();
};

export interface DictationHandlers {
  /** Words so far, including the engine's not-yet-final guess, so the box fills as the user talks. */
  onText: (text: string, isFinal: boolean) => void;
  onEnd: () => void;
  /** 'not-allowed' means the microphone was denied — the only error worth telling the user about. */
  onError: (error: string) => void;
}

/**
 * Start dictation in one language. Returns a stop function, or null when this browser cannot listen.
 *
 * `interimResults` is on so the question box fills as the user speaks: without it a long sentence looks
 * like nothing is happening until the engine decides you have finished.
 */
export const startDictation = (locale: string, handlers: DictationHandlers): (() => void) | null => {
  const Ctor = recognitionCtor();
  if (!Ctor) return null;

  const recognition = new Ctor();
  recognition.lang = locale;
  recognition.interimResults = true;
  recognition.continuous = false;

  recognition.onresult = (event: any) => {
    let text = '';
    let isFinal = false;
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      text += event.results[i][0].transcript;
      if (event.results[i].isFinal) isFinal = true;
    }
    handlers.onText(text.trim(), isFinal);
  };
  recognition.onerror = (event: any) => handlers.onError(event?.error || 'error');
  recognition.onend = () => handlers.onEnd();

  try {
    recognition.start();
  } catch {
    return null; // already running, or blocked by the browser
  }
  return () => {
    try { recognition.stop(); } catch { /* already stopped */ }
  };
};
